import { useState } from 'react';
import {
  InputLabel,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  Box,
  InputAdornment,
} from '@mui/material';
import { jidsRetailerContactInfo } from '../../../../../data/forms/nrc/retailerInfoChangeDropdownOptions.js';
import { yesNoOptions } from '../../../../../data/forms/generalDropdownOptions.js';
import RetailerLookupButton from '../retailerLookupButton.js';
import UpdateStoreHoursModal from '../updateStoreHoursModal.js';
import axios from 'axios';

const storeHoursDisabledJurisdictions = [
  'California',
  'Georgia',
  'Kentucky',
  'Michigan',
  'North Carolina',
  'New York',
  'South Carolina',
  'Wisconsin',
];

const RetailerInfoChangeForm = ({
  translations,
  user,
  handleFormSelectionReset,
  setSnackbarMessage,
  setIsSnackbarOpen,
  setSnackbarSeverity,
}) => {
  const [form, setForm] = useState({
    jurisdiction: '',
    retailerId: '',
    retailerName: '',
    address: '',
    phoneNumber: '',
    updateStoreHours: '',
    updatedHours: {
      sundayOpen: '',
      sundayClose: '',
      mondayOpen: '',
      mondayClose: '',
      tuesdayOpen: '',
      tuesdayClose: '',
      wednesdayOpen: '',
      wednesdayClose: '',
      thursdayOpen: '',
      thursdayClose: '',
      fridayOpen: '',
      fridayClose: '',
      saturdayOpen: '',
      saturdayClose: '',
    },
    updatePhone: '',
    newPhone: '',
    terminal: '',
    associatedTerminals: '',
    requestedPasscode: '',
    oldPasscode: '',
    newPasscode: '',
    requestedBy: '',
    managerPasscode: '',
    comments: '',
    email: user.email,
    emailAddress: user.email,
    displayName: user.displayName,
    userId: user.userId,
  });

  const storeHoursDisabled = storeHoursDisabledJurisdictions.includes(
    form.jurisdiction,
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdateStoreHoursModalOpen, setIsUpdateStoreHoursModalOpen] =
    useState(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,
    });

    if (
      name === 'jurisdiction' &&
      storeHoursDisabledJurisdictions.includes(value)
    ) {
      setForm(prevForm => ({
        ...prevForm,
        jurisdiction: value,
        updateStoreHours: '',
        updatedHours: {
          sundayOpen: '',
          sundayClose: '',
          mondayOpen: '',
          mondayClose: '',
          tuesdayOpen: '',
          tuesdayClose: '',
          wednesdayOpen: '',
          wednesdayClose: '',
          thursdayOpen: '',
          thursdayClose: '',
          fridayOpen: '',
          fridayClose: '',
          saturdayOpen: '',
          saturdayClose: '',
        },
      }));
      return;
    }

    if (name === 'updateStoreHours' && value === 'No') {
      setForm(prevForm => ({
        ...prevForm,
        updatedHours: {
          sunday: { open: '', close: '' },
          monday: { open: '', close: '' },
          tuesday: { open: '', close: '' },
          wednesday: { open: '', close: '' },
          thursday: { open: '', close: '' },
          friday: { open: '', close: '' },
          saturday: { open: '', close: '' },
        },
      }));
    }

    if (name === 'updatePhone' && value === 'No') {
      setForm(prevForm => ({
        ...prevForm,
        newPhone: '',
      }));
    }

    if (name === 'requestedPasscode' && value === 'No') {
      setForm(prevForm => ({
        ...prevForm,
        oldPasscode: '',
        newPasscode: '',
        requestedBy: '',
        managerPasscode: '',
      }));
    }
  };

  const handleUpdateStoreHoursModalOpenClose = () => {
    setIsUpdateStoreHoursModalOpen(!isUpdateStoreHoursModalOpen);
  };

  const handleClear = () => {
    setForm({
      ...form,
      jurisdiction: '',
      retailerId: '',
      retailerName: '',
      address: '',
      phoneNumber: '',
      updateStoreHours: '',
      updatedHours: {
        sundayOpen: '',
        sundayClose: '',
        mondayOpen: '',
        mondayClose: '',
        tuesdayOpen: '',
        tuesdayClose: '',
        wednesdayOpen: '',
        wednesdayClose: '',
        thursdayOpen: '',
        thursdayClose: '',
        fridayOpen: '',
        fridayClose: '',
        saturdayOpen: '',
        saturdayClose: '',
      },
      updatePhone: '',
      newPhone: '',
      terminal: '',
      associatedTerminals: '',
      requestedPasscode: '',
      oldPasscode: '',
      newPasscode: '',
      requestedBy: '',
      managerPasscode: '',
      comments: '',
    });
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await axios({
        method: 'POST',
        url: `${process.env.REACT_APP_ENDPOINT_FORMS}/uscc/retailerInfoChange/submit`,
        data: {
          ...form,
        },
      });
      setSnackbarMessage('Form submitted successfully!');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
      handleClear();
      handleFormSelectionReset();
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbarMessage(
        'There was an error submitting the form. Please try again. If the problem persists, contact support.'
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    const {
      jurisdiction,
      retailerId,
      retailerName,
      address,
      phoneNumber,
      updateStoreHours,
      updatePhone,
      newPhone,
      requestedPasscode,
      requestedBy,
      managerPasscode,
      oldPasscode,
      newPasscode,
    } = form;

    if (
      (!updateStoreHours && !storeHoursDisabled) ||
      !updatePhone ||
      !requestedPasscode ||
      !jurisdiction ||
      !retailerId ||
      !retailerName ||
      !address ||
      !phoneNumber
    ) {
      return false;
    }

    if (updatePhone === 'Yes' && !newPhone) {
      return false;
    }

    if (requestedPasscode === 'Yes') {
      if (!requestedBy || !managerPasscode || !oldPasscode || !newPasscode) {
        return false;
      }
    }

    return true;
  };

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{
          display: 'flex',
          justifyItems: 'center',
          width: { xs: 'auto', md: 'var(--forms-layout-width, 90vw)' },
        }}
      >
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl
            required
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>
              {translations.jurisdiction || 'Jurisdiction'}
            </InputLabel>
            <Select
              value={form.jurisdiction}
              label={translations.jurisdiction || 'Jurisdiction'}
              onChange={handleChange}
              name='jurisdiction'
            >
              {jidsRetailerContactInfo.map(jid => (
                <MenuItem value={jid}>{jid}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label='Retailer Name'
            name='retailerName'
            required
            value={form.retailerName}
            onChange={handleChange}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <TextField
            label='Phone Number'
            name='phoneNumber'
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            value={form.phoneNumber}
            onChange={handleChange}
            required
          />
          <FormControl
            required
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>{'Update Phone'}</InputLabel>
            <Select
              value={form.updatePhone}
              label={'Update Phone'}
              onChange={handleChange}
              name='updatePhone'
            >
              {yesNoOptions.map(jid => (
                <MenuItem value={jid}>{jid}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label='Terminal'
            name='terminal'
            value={form.terminal}
            onChange={handleChange}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <FormControl
            required
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>{'Requested Passcode'}</InputLabel>
            <Select
              value={form.requestedPasscode}
              label={'Requested Passcode'}
              onChange={handleChange}
              name='requestedPasscode'
            >
              {yesNoOptions.map(jid => (
                <MenuItem value={jid}>{jid}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip
            title={
              form.updatePhone !== 'Yes'
                ? "Please select 'Yes' on Requested Passcode to enable this field"
                : ''
            }
            arrow
          >
            <TextField
              label='Requested By'
              name='requestedBy'
              disabled={form.requestedPasscode !== 'Yes'}
              required={form.requestedPasscode === 'Yes'}
              value={form.requestedBy}
              onChange={handleChange}
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
            />
          </Tooltip>
          <Tooltip
            title={
              form.updatePhone !== 'Yes'
                ? "Please select 'Yes' on Requested Passcode to enable this field"
                : ''
            }
            arrow
          >
            <FormControl
              disabled={form.requestedPasscode !== 'Yes'}
              required={form.requestedPasscode === 'Yes'}
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
            >
              <InputLabel>{'Manager Passcode'}</InputLabel>
              <Select
                value={form.managerPasscode}
                label={'Manager Passcode'}
                onChange={handleChange}
                name='managerPasscode'
              >
                {yesNoOptions.map(jid => (
                  <MenuItem value={jid}>{jid}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Tooltip>
          <TextField
            label='Associate Name'
            disabled
            value={`${user.firstName} ${user.lastName}`}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Tooltip
            title={'Enter a Retailer ID (numeric values only) to lookup'}
            arrow
          >
            <TextField
              label='Retailer ID'
              required
              name='retailerId'
              fullWidth
              value={form.retailerId}
              onChange={handleChange}
              sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
              type='number'
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <RetailerLookupButton form={form} setForm={setForm} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Tooltip>
          <TextField
            label='Address'
            name='address'
            required
            value={form.address}
            onChange={handleChange}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <FormControl
            required={!storeHoursDisabled}
            disabled={storeHoursDisabled}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>{'Update Store Hours'}</InputLabel>
            <Select
              value={form.updateStoreHours}
              label={'Update Store Hours'}
              onChange={handleChange}
              name='updateStoreHours'
              endAdornment={
                <InputAdornment position='end'>
                  <Tooltip
                    title={
                      form.updateStoreHours !== 'Yes'
                        ? "Please select 'Yes' on Update Store Hours to enable"
                        : ''
                    }
                  >
                    <Box>
                      <Button
                        variant='contained'
                        disabled={form.updateStoreHours !== 'Yes'}
                        sx={{
                          backgroundColor: 'var(--procyon-green)',
                          color: '#000000',
                          fontFamily: 'BrightstarSans-Regular',
                        }}
                        onClick={handleUpdateStoreHoursModalOpenClose}
                      >
                        Update Store Hours
                      </Button>
                    </Box>
                  </Tooltip>
                </InputAdornment>
              }
            >
              {yesNoOptions.map(jid => (
                <MenuItem value={jid}>{jid}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip
            title={
              form.updatePhone !== 'Yes'
                ? "Please select 'Yes' on Update Phone to enable this field"
                : ''
            }
            arrow
          >
            <TextField
              disabled={form.updatePhone !== 'Yes'}
              required={form.updatePhone === 'Yes'}
              label='New Phone Number'
              name='newPhone'
              fullWidth
              value={form.newPhone}
              onChange={handleChange}
              sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
            />
          </Tooltip>
          <TextField
            label='Associated Terminals'
            name='associatedTerminals'
            value={form.associatedTerminals}
            onChange={handleChange}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <Tooltip
            title={
              form.updatePhone !== 'Yes'
                ? "Please select 'Yes' on Requested Passcode to enable this field"
                : ''
            }
            arrow
          >
            <TextField
              label='Old Passcode'
              name='oldPasscode'
              disabled={form.requestedPasscode !== 'Yes'}
              required={form.requestedPasscode === 'Yes'}
              value={form.oldPasscode}
              onChange={handleChange}
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
            />
          </Tooltip>
          <Tooltip
            title={
              form.updatePhone !== 'Yes'
                ? "Please select 'Yes' on Requested Passcode to enable this field"
                : ''
            }
            arrow
          >
            <TextField
              label='New Passcode'
              name='newPasscode'
              disabled={form.requestedPasscode !== 'Yes'}
              required={form.requestedPasscode === 'Yes'}
              value={form.newPasscode}
              onChange={handleChange}
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
            />
          </Tooltip>
          <TextField
            label='Comments'
            name='comments'
            value={form.comments}
            onChange={handleChange}
            multiline
            rows={4.1}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
        </Grid>
        <Grid
          size={12}
          sx={{
            marginTop: '20px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant='contained'
            sx={{
              backgroundColor: 'var(--aldebaran-yellow)',
              color: '#000000',
              fontFamily: 'BrightstarSans-Regular',
              width: '10%',
            }}
            onClick={handleClear}
          >
            Clear
          </Button>
          <Tooltip
            title={
              !isFormValid()
                ? 'Please fill in all required fields to enable the Submit button'
                : ''
            }
            arrow
          >
            <Box>
              <Button
                disabled={!isFormValid() || isSubmitting}
                variant='contained'
                sx={{
                  backgroundColor: 'var(--vega-blue)',
                  fontFamily: 'BrightstarSans-Regular',
                  width: '100%',
                }}
                onClick={handleSubmit}
                loading={isSubmitting}
              >
                {'Submit'}
              </Button>
            </Box>
          </Tooltip>
        </Grid>
      </Grid>
      {isUpdateStoreHoursModalOpen && (
        <UpdateStoreHoursModal
          isOpen={isUpdateStoreHoursModalOpen}
          handleClose={handleUpdateStoreHoursModalOpenClose}
          updatedHours={form.updatedHours}
          setForm={setForm}
        />
      )}
    </>
  );
};

export default RetailerInfoChangeForm;
